import first from 'lodash/first'
import flatten from 'lodash/flatten'
import last from 'lodash/last'
import React, { useEffect, useMemo, useState } from 'react'
import type { Location } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import { LIVE_LIBRARIES } from '@disco/constants/FeatureTypes'
import { clientLibraryClientsLoad } from '@disco/state-management/actions/client-library-client-actions'
import { clientLibraryStatsLoad } from '@disco/state-management/actions/client-library-stats-actions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getClientLibrary } from '@disco/state-management/selectors/client-libraries'
import {
  getTrialStartDate,
  isClientLibrariesTrial,
} from '@disco/state-management/selectors/user'
import { useComposerInsteadOfArtist } from '@disco/utils/composerInsteadOfArtist'
import { getDateRange } from '@disco/utils/date'
import dayjs from '@disco/utils/dayjs'

import type { ClientLibrary } from 'types/client-library'
import type { State } from 'types/redux'

import emptyStats1x from 'assets/images/empty-stats.png'
import emptyStats2x from 'assets/images/empty-stats@2x.png'

import Chart from 'components/common/chart'
import DateRangePicker from 'components/common/date-range-picker'
import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import { TabNav } from 'components/ui'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import Counter from 'components/ui/counter'
import DataTable from 'components/ui/data-table'
import EmptyState from 'components/ui/empty-state'
import Heading from 'components/ui/heading'
import InlineLink from 'components/ui/inline-link'
import PageContainer from 'components/ui/page-container'

import { useDispatch, useSelector } from 'hooks/use-redux'

const CHART_COLORS = ['#ae61ef', '#4a90e2', '#47c1bf']
const CHART_LABELS = {
  activity_chart: ['Sessions', 'Streams', 'Downloads'],
  users_chart: [
    // 'Requests for Access',
    'Active Users',
    'Sessions',
  ],
}
const SUMMARY = {
  active_users: 'Active users',
  access_requests: 'Access requests',
  sessions: 'Sessions',
  streams: 'Streams',
  downloads: 'Downloads',
}

const getChartData = (charts, dates, key) => {
  if (!charts || !charts[key]) {
    return null
  }

  const data = Object.entries(charts[key]['per_day']).reduce((acc, item) => {
    const x = dayjs(item[0])
    const y = item[1]
    const dateStr = x.format('MM/DD')

    return {
      ...acc,
      [dateStr]: {
        x,
        y,
      },
    }
  }, {})
  const days = getDateRange(dates.start, dates.end)
  const byDate = days.reduce((acc, day) => {
    const dateStr = day.format('MM/DD')
    acc[dateStr] = data[dateStr] || getZeroValue(day) // eslint-disable-line no-param-reassign

    return acc
  }, {})
  const values = Object.values(byDate)

  if (values.length > 2) {
    const groups = values.reduce((prev: any[], curr: any) => {
      if (prev.length && curr.y === prev[prev.length - 1][0].y) {
        prev[prev.length - 1].push(curr)
      } else {
        prev.push([curr])
      }

      return prev
    }, [])
    const filtered = Object.values(groups).map((group: any) =>
      group.length > 1 ? [first(group), last(group)] : last(group),
    )
    const result = flatten(filtered)
    return result
  } else {
    return values
  }
}

const getChartDateRange = (startDate, endDate) => {
  const rangeArray = getDateRange(startDate, endDate)
  const rangeStep = Math.floor((rangeArray.length - 2) / 8)
  let stepArray = []

  if (rangeStep > 1) {
    stepArray.push(rangeArray[0])

    for (
      let i = rangeStep;
      i < rangeArray.length - rangeStep;
      i = i + rangeStep
    ) {
      stepArray.push(rangeArray[i])
    }

    stepArray.push(dayjs(endDate))
  } else {
    stepArray = rangeArray
  }

  return {
    start: startDate,
    end: endDate,
    range: stepArray,
  }
}

const getZeroValue = x => ({
  x,
  y: 0,
})

type Props = {
  location: Location
}

const LibrariesLibraryStats = (props: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const { libraryId } = useParams<{ libraryId: string }>()
  const libraryIdNum: number = parseInt(libraryId!, 10)
  const library: ClientLibrary = useSelector((state: State) =>
    getClientLibrary(state, libraryIdNum),
  )
  const clientLibrariesTrial: boolean = useSelector(isClientLibrariesTrial)
  const liveLibrariesEnabled: boolean = useFeatureEnabled(LIVE_LIBRARIES)
  const trialStartDate: Date = useSelector(getTrialStartDate)
  useEffect(() => {
    dispatch(clientLibraryClientsLoad(libraryIdNum))
  }, [libraryIdNum])
  const isStatsHidden: boolean =
    !library?.stats?.loading &&
    library?.stats?.sessions?.total === 0 &&
    !library?.clients?.loading &&
    library?.clients?.list.length === 0
  const dateRangeAllTime = {
    period_start: trialStartDate,
    period_end: new Date(),
  }
  const [dateRange, setDateRange] = useState(dateRangeAllTime)
  const typeData = useComposerInsteadOfArtist().value

  const handleDateChange = (startDate, endDate) => {
    setDateRange({
      period_start: startDate || endDate,
      period_end: endDate || startDate,
    })
  }

  const dates = useMemo(() => {
    return getChartDateRange(dateRange.period_start, dateRange.period_end)
  }, [dateRange])
  useEffect(() => {
    dispatch(
      clientLibraryStatsLoad(libraryIdNum, {
        period_start: dayjs(dateRange['period_start']).format('YYYY-MM-DD'),
        period_end: dayjs(dateRange['period_end']).format('YYYY-MM-DD'),
      }),
    )
  }, [libraryIdNum, dateRange])
  const [selectedTab, setSelectedTab] = useState('activity_chart')
  const chartData = {
    activity_chart:
      library && library.stats && !library.stats.loading
        ? [
            getChartData(library.stats, dates, 'sessions'),
            getChartData(library.stats, dates, 'streams'),
            getChartData(library.stats, dates, 'downloads'),
          ]
        : [],
    users_chart:
      library && library.stats && !library.stats.loading
        ? [
            getChartData(library.stats, dates, 'active_users'),
            getChartData(library.stats, dates, 'sessions'),
          ]
        : [],
  }
  let reasonInvitesDisabled = undefined

  if (clientLibrariesTrial) {
    reasonInvitesDisabled = 'You’re using the trial version of Discovery Suite.'
  } else if (!liveLibrariesEnabled) {
    reasonInvitesDisabled =
      'To invite clients, the Catalog needs to be live. Making Catalogs live requires a Discovery Suite subscription.'
  } else if (!library?.is_live) {
    reasonInvitesDisabled =
      'To invite clients to your Catalog, please set the Catalog to Published using the switch at the top right.'
  }

  const renderStats = () =>
    library?.stats?.sessions?.total === 0 ? (
      <EmptyState>
        <img
          srcSet={`${emptyStats1x}, ${emptyStats2x} 2x`}
          width="195"
          height="174"
        />
        <p>Sorry! There’s no activity available for this period.</p>
      </EmptyState>
    ) : (
      <>
        {library?.stats &&
          !library?.stats?.loading &&
          Object.keys(SUMMARY).every((key: string) => {
            if (library && library.stats && library.stats[key]) {
              return library.stats[key].total === 0 || library.stats[key] === 0
            }

            return true
          }) && (
            <Row columnsSize={[12]}>
              <div>
                {!reasonInvitesDisabled ? (
                  <Alert>
                    No activity yet! Generate some activity by inviting some
                    clients to your library.
                  </Alert>
                ) : (
                  <Alert>
                    No activity yet! Generate some activity by{' '}
                    <InlineLink to={`/catalogs/${libraryIdNum}/clients/invite`}>
                      inviting some clients to your library.
                    </InlineLink>
                  </Alert>
                )}
              </div>
            </Row>
          )}
        <Row columnsSize={[2, 2, 2, 2, 2]}>
          {Object.keys(SUMMARY).map((key: string, index: number) => {
            const getValue = stats => {
              if (stats && stats[key]) {
                return stats[key].total ?? stats[key]
              }

              return '-'
            }

            return (
              <div key={index}>
                <Counter
                  caption={SUMMARY[key]}
                  smallValueFont={true}
                  value={
                    library &&
                    (library?.stats?.loading ? '-' : getValue(library.stats))
                  }
                />
              </div>
            )
          })}
        </Row>
        <Row columnsSize={[12]}>
          <div>
            <TabNav
              className="Tabs--modal"
              items={[
                {
                  label: 'Users',
                  active: selectedTab === 'users_chart',
                  onClick: () => setSelectedTab('users_chart'),
                },
                {
                  label: 'Activity',
                  active: selectedTab === 'activity_chart',
                  onClick: () => setSelectedTab('activity_chart'),
                },
              ]}
            />
            <Chart
              colors={CHART_COLORS}
              data={chartData[selectedTab]}
              dates={dates}
              labels={CHART_LABELS[selectedTab]}
              loading={library?.stats?.loading}
            />
          </div>
        </Row>
        {selectedTab === 'activity_chart' && (
          <>
            <Row columnsSize={[6, 6]}>
              <div>
                <Heading size={500}>Most streamed tracks</Heading>
                <DataTable
                  appearance="plain"
                  columns={[
                    {
                      id: 'name',
                      name: 'Track',
                    },
                    {
                      id: 'count',
                      name: 'Streams',
                      width: 'content',
                    },
                  ]}
                  data={
                    library &&
                    library.stats &&
                    library.stats['most_streamed_tracks']
                  }
                  loading={library?.stats?.loading}
                />
              </div>
              <div>
                <Heading size={500}>Most streamed playlists</Heading>
                <DataTable
                  appearance="plain"
                  columns={[
                    {
                      id: 'name',
                      name: 'Playlist',
                    },
                    {
                      id: 'count',
                      name: 'Streams',
                      width: 'content',
                    },
                  ]}
                  data={
                    library &&
                    library.stats &&
                    library.stats['most_streamed_playlists']
                  }
                  loading={library?.stats?.loading}
                />
              </div>
            </Row>
            <Row columnsSize={[6, 6]}>
              <div>
                <Heading size={500}>Most streamed {typeData}s</Heading>
                <DataTable
                  appearance="plain"
                  columns={[
                    {
                      id: 'name',
                      name: typeData,
                    },
                    {
                      id: 'count',
                      name: 'Streams',
                      width: 'content',
                    },
                  ]}
                  data={
                    library &&
                    library.stats &&
                    library.stats['most_streamed_artists']
                  }
                  loading={library?.stats?.loading}
                />
              </div>
              <div>
                <Heading size={500}>Most downloaded tracks</Heading>
                <DataTable
                  appearance="plain"
                  columns={[
                    {
                      id: 'name',
                      name: 'Tracks',
                    },
                    {
                      id: 'count',
                      name: 'Downloads',
                      width: 'content',
                    },
                  ]}
                  data={
                    library &&
                    library.stats &&
                    library.stats['most_downloaded_tracks']
                  }
                  loading={library?.stats?.loading}
                />
              </div>
            </Row>
            <Row columnsSize={[6, 6]}>
              <div>
                <Heading size={500}>Most downloaded playlists</Heading>
                <DataTable
                  appearance="plain"
                  columns={[
                    {
                      id: 'name',
                      name: 'Playlists',
                    },
                    {
                      id: 'count',
                      name: 'Downloads',
                      width: 'content',
                    },
                  ]}
                  data={
                    library &&
                    library.stats &&
                    library.stats['most_downloaded_playlists']
                  }
                  loading={library?.stats?.loading}
                />
              </div>
              <div>
                <Heading size={500}>Most downloaded {typeData}s</Heading>
                <DataTable
                  appearance="plain"
                  columns={[
                    {
                      id: 'name',
                      name: `${typeData}s`,
                    },
                    {
                      id: 'count',
                      name: 'Downloads',
                      width: 'content',
                    },
                  ]}
                  data={
                    library &&
                    library.stats &&
                    library.stats['most_downloaded_artists']
                  }
                  loading={library?.stats?.loading}
                />
              </div>
            </Row>
          </>
        )}
        {selectedTab === 'users_chart' && (
          <Row columnsSize={[6, 6]}>
            <div>
              <Heading size={500}>Most active users</Heading>
              <DataTable
                appearance="plain"
                columns={[
                  {
                    name: 'User',
                    render: ({ name, email }) => name || email,
                  },
                  {
                    id: 'count',
                    name: 'Sessions',
                    width: 'content',
                  },
                ]}
                data={
                  library && library.stats && library.stats['most_active_users']
                }
                loading={library?.stats?.loading}
              />
            </div>
          </Row>
        )}
      </>
    )

  return (
    <PageContainer.Section
      title="Stats"
      action={
        <>
          <label className="Form_Label Stats_Label">Select period</label>
          <DateRangePicker
            disabled={library?.loading || isStatsHidden} // TODO: Unify DateRangePicker styles
            dropdownOpenClassName="StatsDateRange-dropdown-open"
            minDate={dateRangeAllTime.period_start}
            maxDate={dateRangeAllTime.period_end}
            startDate={dateRange['period_start']}
            endDate={dateRange['period_end']}
            showPredefinedRanges
            autoApply
            onChange={handleDateChange}
          />
        </>
      }
    >
      {library && !library['loading'] && (
        <DocumentHeadTags title={`${library['name']} | Stats | DISCO`} />
      )}
      {isStatsHidden ? (
        <EmptyState>
          <img
            srcSet={`${emptyStats1x}, ${emptyStats2x} 2x`}
            width="195"
            height="174"
          />
          <p>
            Here you’ll see detailed stats for how clients are using your
            Catalogs stats on requests for access, plays, downloads and more.
            To start generating some activity, invite clients via the button
            below.
          </p>
          <Button // TODO: merge duplicates of Invite clients button in /clients
            appearance="primary"
            caption="Invite clients"
            disabled={Boolean(reasonInvitesDisabled)}
            link={{
              pathname: `/catalogs/${libraryIdNum}/clients/invite`,
              state: {
                modal: true,
                returnTo: props.location.pathname,
              },
            }}
            tooltip={reasonInvitesDisabled}
          />
        </EmptyState>
      ) : (
        renderStats()
      )}
    </PageContainer.Section>
  )
}

export default LibrariesLibraryStats
